import { AdminLayout } from "../../components/layout/AdminLayout";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Shield, Key, FileText, Save, AlertTriangle, Wifi, Copy, CheckCircle } from "lucide-react";
import api from "../../lib/api";


const SSOConfigPage = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [copied, setCopied] = useState("");

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get("/sso/config");
      setConfig(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put("/sso/config", config);
      toast.success("SSO configuration saved");
    } catch (e) { toast.error("Failed to save"); }
    setSaving(false);
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post("/sso/test", {});
      setTestResult(res.data);
      if (res.data.success) toast.success("IdP connection successful");
      else toast.error(res.data.error || "Connection failed");
    } catch (e) { toast.error("Test failed"); }
    setTesting(false);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(""), 2000);
  };

  if (loading || !config) return <div className="p-6 space-y-4">{[1,2].map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />)}</div>;

  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const spAcsUrl = `${baseUrl}/api/sso/acs`;
  const spMetaUrl = `${baseUrl}/api/sso/metadata`;

  return (
    <AdminLayout title="SSO Configuration">
    <div className="p-6 space-y-6" data-testid="sso-config-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Single Sign-On (SAML 2.0)</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure SAML-based SSO for your organization</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">SSO Configuration</p>
          <p className="text-sm text-amber-700 mt-0.5">SAML SSO requires your Identity Provider (IdP) configuration details. Contact your IT administrator for these values.</p>
        </div>
      </div>

      {/* Enable Toggle */}
      <div className="bg-background border rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-600" />
            <div>
              <h3 className="font-bold">SAML 2.0 SSO</h3>
              <p className="text-sm text-muted-foreground">Enable single sign-on for your users</p>
            </div>
          </div>
          <button onClick={() => setConfig({...config, is_enabled: !config.is_enabled})}
            className={`w-12 h-6 rounded-full transition-colors relative ${config.is_enabled ? 'bg-green-500' : 'bg-muted'}`} data-testid="sso-toggle">
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${config.is_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
          </button>
        </div>
      </div>

      {/* Config Fields */}
      <div className="bg-background border rounded-xl p-5 shadow-sm space-y-4">
        <h3 className="font-bold flex items-center gap-2"><Key className="w-4 h-4" /> Identity Provider Settings</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Entity ID (Issuer)</label>
            <Input value={config.entity_id || ""} onChange={e => setConfig({...config, entity_id: e.target.value})}
              placeholder="https://idp.example.com/metadata" className="mt-1" data-testid="sso-entity-id" />
          </div>
          <div>
            <label className="text-sm font-medium">SSO URL (Login Endpoint)</label>
            <Input value={config.sso_url || ""} onChange={e => setConfig({...config, sso_url: e.target.value})}
              placeholder="https://idp.example.com/sso" className="mt-1" data-testid="sso-url" />
          </div>
          <div>
            <label className="text-sm font-medium">SLO URL (Logout Endpoint)</label>
            <Input value={config.slo_url || ""} onChange={e => setConfig({...config, slo_url: e.target.value})}
              placeholder="https://idp.example.com/slo (optional)" className="mt-1" data-testid="sso-slo-url" />
          </div>
          <div>
            <label className="text-sm font-medium">Default Role for New Users</label>
            <select value={config.default_role || "learner"} onChange={e => setConfig({...config, default_role: e.target.value})}
              className="w-full mt-1 h-10 px-3 border rounded-md bg-background text-sm" data-testid="sso-default-role">
              <option value="learner">Learner</option>
              <option value="manager">Manager</option>
              <option value="tenant_admin">Tenant Admin</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">X.509 Certificate</label>
          <textarea className="w-full mt-1 rounded-md border p-3 text-sm bg-background min-h-[100px] font-mono resize-y"
            value={config.certificate || ""} onChange={e => setConfig({...config, certificate: e.target.value})}
            placeholder="-----BEGIN CERTIFICATE-----&#10;MIIBkTCB+wIJAJ...&#10;-----END CERTIFICATE-----" data-testid="sso-cert" />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={config.auto_provision !== false}
            onChange={e => setConfig({...config, auto_provision: e.target.checked})}
            className="rounded" data-testid="sso-auto-provision" />
          <label className="text-sm">Auto-provision new users on first SSO login</label>
        </div>
      </div>

      {/* Attribute Mapping */}
      <div className="bg-background border rounded-xl p-5 shadow-sm space-y-4">
        <h3 className="font-bold flex items-center gap-2"><FileText className="w-4 h-4" /> Attribute Mapping</h3>
        <p className="text-sm text-muted-foreground">Map SAML assertion attributes to user fields</p>
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(config.attribute_mapping || {}).map(([key, value]) => (
            <div key={key}>
              <label className="text-sm font-medium capitalize">{key.replace("_", " ")}</label>
              <Input value={value} onChange={e => setConfig({...config, attribute_mapping: {...config.attribute_mapping, [key]: e.target.value}})}
                className="mt-1" data-testid={`sso-attr-${key}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Service Provider Info */}
      <div className="bg-background border rounded-xl p-5 shadow-sm space-y-3">
        <h3 className="font-bold">Service Provider (SP) Details</h3>
        <p className="text-sm text-muted-foreground">Provide these values to your Identity Provider</p>
        <div className="space-y-2">
          {[
            { label: "ACS URL", value: spAcsUrl },
            { label: "Entity ID / Metadata URL", value: spMetaUrl },
          ].map(item => (
            <div key={item.label} className="flex items-center justify-between bg-muted/50 rounded-lg px-4 py-2.5">
              <div>
                <span className="text-xs text-muted-foreground">{item.label}</span>
                <p className="text-sm font-mono">{item.value}</p>
              </div>
              <button onClick={() => copyToClipboard(item.value, item.label)} className="text-muted-foreground hover:text-foreground transition-colors" data-testid={`copy-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                {copied === item.label ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Test Result */}
      {testResult && (
        <div className={`border rounded-xl p-4 ${testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="sso-test-result">
          <p className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
            {testResult.success ? "Connection Successful" : "Connection Failed"}
          </p>
          <p className={`text-sm mt-1 ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
            {testResult.message || testResult.error}
          </p>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={testConnection} disabled={testing || !config.sso_url} data-testid="test-sso-btn">
          <Wifi className="w-4 h-4 mr-2" /> {testing ? "Testing..." : "Test Connection"}
        </Button>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={saveConfig} disabled={saving} data-testid="save-sso-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Configuration"}
        </Button>
      </div>
    </div>
    </AdminLayout>
  );
};

export default SSOConfigPage;
